<template>
  <v-container fluid class="auth-wrapper pa-0">
    <v-row no-gutters class="fill-height min-vh-100">
      <!-- Left Side: Visual Pane (Modern Enterprise Slate) -->
      <v-col cols="12" md="6" class="visual-pane d-none d-md-flex align-center justify-center">
        <div class="visual-content pa-12">
          <div class="integration-display mb-12">
            <div class="d-flex align-center">
              <v-img
                max-width="180"
                src="@/assets/runflow_logo.png"
                contain
              ></v-img>
              <template v-if="relBaseEnabled">
                <div class="mx-5 white--text opacity-20 text-h4 font-weight-light">+</div>
                <v-img
                  max-width="140"
                  src="@/assets/relbase_logo.webp"
                  contain
                  class="relbase-logo-entry"
                ></v-img>
              </template>
            </div>
            <div v-if="relBaseEnabled" class="mt-4">
              <v-chip dark small color="rgba(53, 189, 200, 0.15)" class="px-3 py-0 accent-text caption font-weight-bold">
                <v-icon x-small left color="#35BDC8">fas fa-sync-alt</v-icon>
                Sincronización Nativa Activa
              </v-chip>
            </div>
          </div>
          <div class="brand-message">
            <h1 class="text-h2 font-weight-bold white--text mb-6 raleway-font tracking-tight">
              Potencia tu <span class="accent-text">flujo</span> de trabajo.
            </h1>
            <p class="text-h6 white--text opacity-70 lato-font font-weight-light lh-relaxed">
              La ventana única para el ciclo completo de <br>producción y facturación industrial.
            </p>
          </div>
          
          <div class="trust-indicator d-flex mt-12 pt-12 border-top-ghost">
            <div class="d-flex flex-column">
              <span class="white--text caption font-weight-black lato-font tracking-wider text-uppercase">Factory Systems SpA</span>
              <span v-if="relBaseEnabled" class="grey--text text--lighten-1 caption opacity-60">Integrado con Relbase</span>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Right Side: Login Form (Flat & Crispy) -->
      <v-col cols="12" md="6" class="form-pane d-flex align-center justify-center">
        <div class="auth-card-container pa-8 pa-sm-16">
          <!-- Mobile Logo -->
          <div class="text-center mb-10 d-md-none">
            <v-img
              max-width="140"
              class="mx-auto"
              src="@/assets/runflow_logo.png"
            ></v-img>
          </div>

          <div class="mb-10 text-center">
            <div v-if="logo" class="company-logo-login-container mb-8 pa-4 d-inline-block">
              <v-img
                :src="logo"
                contain
                max-height="90"
                max-width="220"
                class="mx-auto"
              ></v-img>
            </div>
            <h2 class="text-h4 font-weight-bold slate-text raleway-font mb-2">Bienvenido</h2>
            <p class="grey--text text--darken-1 lato-font font-weight-medium">Ingresa tus credenciales de acceso</p>
          </div>

          <v-form @submit.prevent="login">
            <div class="input-group mb-6">
              <label class="input-label mb-2 d-block slate-text font-weight-bold text-uppercase caption tracking-wider">Correo Electrónico</label>
              <v-text-field
                v-model="email"
                placeholder="usuario@empresa.com"
                outlined
                flat
                dense
                class="modern-input-field"
                prepend-inner-icon="mdi-email-outline"
                type="email"
                hide-details="auto"
                color="#35BDC8"
              ></v-text-field>
            </div>

            <div class="input-group mb-8">
              <div class="d-flex align-center justify-space-between mb-2">
                <label class="input-label d-block slate-text font-weight-bold text-uppercase caption tracking-wider">Contraseña</label>
                <router-link to="/auth/password-reset/email" class="caption accent-link font-weight-bold text-none">¿Olvidaste tu contraseña?</router-link>
              </div>
              <v-text-field
                v-model="password"
                placeholder="••••••••"
                outlined
                flat
                dense
                class="modern-input-field"
                prepend-inner-icon="mdi-lock-outline"
                :append-icon="showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                @click:append="showPassword = !showPassword"
                :type="showPassword ? 'text' : 'password'"
                hide-details="auto"
                color="#35BDC8"
              ></v-text-field>
            </div>

            <v-btn 
              color="#35BDC8" 
              large 
              block 
              depressed
              type="submit" 
              :loading="loading"
              :disabled="loading"
              class="h-52 rounded-md white--text font-weight-bold text-uppercase tracking-widest login-btn-enterprise"
            >
              Iniciar sesión
              <v-icon right size="16" class="ml-2">fas fa-chevron-right</v-icon>
              <template v-slot:loader>
                <v-progress-circular indeterminate size="22" width="2" color="white"></v-progress-circular>
              </template>
            </v-btn>
          </v-form>

          <div class="mt-16 pt-8 text-center border-top-light">
            <p class="caption grey--text text--darken-1 mb-0 lato-font">
              &copy; {{ new Date().getFullYear() }} <strong>Runflow Evolution 9</strong>. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { login, logout, getLogo, getRelBaseEnabled } from '@/api/auth'
import interpolate from 'interpolate'

export default {
  name: 'Login',
  data: () => ({
    email: '',
    password: '',
    showPassword: false,
    loading: false,
    logo: '',
    relBaseEnabled: false
  }),
  async created() {
    await logout()
    try {
      this.logo = await getLogo()
    } catch (e) {
      console.error("Logo fetch failed", e)
    }
    try {
      this.relBaseEnabled = await getRelBaseEnabled()
    } catch (e) {
      console.error("RelBaseEnabled fetch failed", e)
    }
    console.log("relbase_enabled", this.relBaseEnabled)
  },
  methods: {
    async login() {
      if (!this.email || !this.password) return
      
      this.loading = true
      await login({
        email: this.email,
        password: this.password
      })
      .then(async (user) => {
        this.updateAbility(user)
        // Lleno el store con datos
        await Promise.all([
          this.$store.dispatch('getParameters'),
          this.$store.dispatch('getSellers'),
          this.$store.dispatch('getGroups'),
          this.$store.dispatch('getLines'),
          this.$store.dispatch('getFamilies'),
          this.$store.dispatch('getStages'),
          this.$store.dispatch('getStores'),
          this.$store.dispatch('getIndustries'),
          this.$store.dispatch('getRubros'),
          this.$store.dispatch('getOrigins'),
          this.$store.dispatch('getPaymentRequest')
        ])
        
        this.$router.push('/')
      })
      .catch((error) => {
        console.error("Login error", error)
      })
      .finally(() => {
        this.loading = false
      })
    },
    updateAbility(user) {
      const permissions = user.user_role.permits
      const rules = []
      permissions.forEach(p => {
        const conditionString = p.condition ? interpolate(p.condition, { user }) : '{}'
        rules.push({
          action: p.action,
          subject: p.subject,
          conditions: JSON.parse(conditionString)
        })
      })
      this.$ability.update(rules)
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Raleway:wght@600;700&family=Lato:wght@300;400;700&display=swap');

.raleway-font { font-family: 'Raleway', sans-serif !important; }
.lato-font { font-family: 'Lato', sans-serif !important; }

.auth-wrapper {
  min-height: 100vh;
  background-color: #F8FAFC;
  margin: -12px; /* Compensate for v-container fluid padding in App.vue */
}

.min-vh-100 {
  min-height: 100vh !important;
}

.visual-pane {
  background-color: #1A1F2E; /* Chosen Slate Navy */
  position: relative;
  border-right: 1px solid rgba(255,255,255,0.05);
}

.visual-content {
  z-index: 1;
  max-width: 580px;
}

.accent-text {
  color: #35BDC8; /* Vibrant Turquoise */
}

.tracking-tight { letter-spacing: -1px !important; }
.tracking-widest { letter-spacing: 1.5px !important; }
.tracking-wider { letter-spacing: 0.8px !important; }
.lh-relaxed { line-height: 1.6 !important; }
.opacity-70 { opacity: 0.7; }

.form-pane {
  background-color: #FFFFFF;
}

.auth-card-container {
  width: 100%;
  max-width: 520px;
}

.slate-text {
  color: #1A1F2E;
}

.company-logo-login-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.04);
  border: 1px solid #F1F5F9;
}

.modern-input-field {
  ::v-deep .v-input__control {
    .v-input__slot {
      border-radius: 8px !important; /* Spacing Spec: 8px radius */
      background-color: #F8FAFC !important;
      border: 1px solid #E0E0E0 !important;
      box-shadow: none !important;
      transition: all 0.2s ease;
      
      fieldset { border: none; }
    }
    
    &.v-input--is-focused .v-input__slot {
      background-color: #FFFFFF !important;
      border-color: #35BDC8 !important;
      box-shadow: 0 0 0 3px rgba(53, 189, 200, 0.1) !important;
    }
  }
}

.accent-link {
  color: #35BDC8 !important;
  text-decoration: none;
  &:hover { text-decoration: underline; }
}

.h-52 { height: 52px !important; }
.rounded-md { border-radius: 8px !important; }

.login-btn-enterprise {
  transition: all 0.2s ease;
  box-shadow: none !important;
  &:hover {
    background-color: #2da1ac !important;
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
}

.integration-display {
  .logo-separator {
    color: rgba(255, 255, 255, 0.2);
    font-size: 24px;
    font-weight: 300;
  }
}

.relbase-logo-entry {
  animation: slideInRight 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.border-top-ghost { border-top: 1px solid rgba(255,255,255,0.08); }
.border-top-light { border-top: 1px solid #E0E0E0; }

@media (max-width: 960px) {
  .auth-card-container {
    max-width: 100%;
  }
}
</style>
